import { api } from './client'

// ============================================================
// IPAM — conciliação de endereçamento IP (Fatia 1)
// Rotas: /api/v1/ipam/ambientes, /divergencias, /collect-request
// Permissão: ipam:view (leitura) / ipam:manage (pedir conciliação).
//
// `ipam:view` é permissão de PLATAFORMA e tudo-ou-nada: a tela mostra o
// endereçamento de todos os tenants junto, e por isso NÃO existe escopo por
// tenant aqui — nada de useCurrentTenant nesta feature.
//
// Spec: docs/superpowers/specs/2026-07-29-ipam-conciliacao-enderecamento-design.md
// ============================================================

/**
 * Estado de uma faixa real. Excludente, com precedência definida no backend
 * (`classificar_faixas` em `consoleai/ipam/service.py`):
 *
 * - `vpc_default`        — criada pela AWS (172.31.0.0/16); fora das contagens
 * - `publico`            — fora da RFC 1918; vence rota
 * - `rota_indeterminada` — a leitura do TGW falhou; nem roteada nem sem rota
 * - `vivo_roteado` / `vivo`
 */
export type EstadoFaixa =
  | 'vpc_default'
  | 'publico'
  | 'rota_indeterminada'
  | 'vivo_roteado'
  | 'vivo'

/**
 * Uma faixa de VPC que existe de fato na AWS, já cruzada com o eixo declarado.
 *
 * O eixo declarado é o **registro** de endereçamento. Na Fatia 1 ele é semeado da
 * planilha (proveniência, `ipam_reference.origem`), mas o vocabulário da tela é
 * "registro": na Fatia 2 ele vira `ipam_allocations` com dono e data, e a planilha
 * sai de cena.
 */
/**
 * Vigência da faixa — responde "isto ainda existe?", que é pergunta diferente de
 * `EstadoFaixa` ("como esta faixa se comporta?").
 *
 * - `vigente`       — a última varredura completa reconfirmou a conta
 * - `desatualizada` — a última varredura completa não conseguiu ler a conta. A
 *                     faixa continua contando, com a data do último dado
 *                     confirmado: ninguém confirmou que sumiu
 * - `removida`      — alguém leu a conta com sucesso e a faixa não estava lá.
 *                     NÃO aparece em `ambientes`; vem em `removidas`
 */
export type EstadoVigencia = 'vigente' | 'desatualizada' | 'removida'

export interface Ambiente {
  cidr: string
  conta: string
  conta_nome: string
  regiao: string
  vpc: string
  vpc_nome: string
  default: boolean
  estado: EstadoFaixa
  /** Existe linha de registro declarado cobrindo esta faixa. */
  no_registro: boolean
  /** Da linha declarada que cobre a faixa; `null` quando nenhuma cobre. */
  projeto: string | null
  ambiente: string | null
  /** Só `vigente` ou `desatualizada` — `removida` vem na lista própria. */
  vigencia: EstadoVigencia
  /** Data do último dado confirmado quando `desatualizada`; `''` quando vigente. */
  desde: string
}

/**
 * Faixa que existia e não existe mais: uma execução leu a conta com sucesso e não
 * a encontrou. Fica fora das contagens e do mapa de espaço — mas continua no
 * histórico, porque saber que existiu tem valor.
 *
 * Cobre remoção de CIDR, não só de VPC: a chave inclui o cidr, então bloco
 * secundário desassociado de uma VPC que continua viva também cai aqui.
 */
export interface FaixaRemovida {
  cidr: string
  conta: string
  conta_nome: string
  regiao: string
  vpc: string
  vpc_nome: string
  /** Quando a ausência foi confirmada. */
  desde: string
}

/**
 * Cobertura da última execução. `sem_execucao: true` significa que a coleta
 * nunca rodou — a tela precisa dizer isso, porque tabela vazia sem rótulo é
 * indistinguível de "nada errado por aqui" (§6.1).
 */
export interface Cobertura {
  sem_execucao: boolean
  run_id?: number
  status?: string
  regions?: string[]
  accounts_total?: number
  accounts_ok?: number
  accounts_partial?: number
  accounts_failed?: number
  /** `null` quando a leitura do TGW falhou. */
  routes_read?: number | null
  per_account?: Record<string, { status?: string; erros?: string[]; faixas?: number }>
  started_at?: string
  finished_at?: string | null
}

/** `indeterminadas` = a leitura do TGW falhou nesta execução. */
export type EstadoRotas = 'lidas' | 'indeterminadas'

export interface AmbientesResposta {
  ambientes: Ambiente[]
  /** Faixas que sumiram da AWS. Fora das contagens e do mapa de espaço. */
  removidas: FaixaRemovida[]
  rotas: EstadoRotas
  cobertura: Cobertura
}

/** Tipos de divergência produzidos pelo backend (as 9 categorias do §5). */
export type TipoDivergencia =
  | 'sobreposicao'
  | 'nao_rfc1918'
  | 'vpc_default'
  | 'sem_rota_no_hub'
  | 'vpc_sem_registro'
  | 'registro_sem_vpc'
  | 'existe_em_outro_bloco'
  | 'fora_da_organizacao'
  | 'fora_do_padrao'

/** Onde um registro declarado existe de fato, sob outro bloco. */
export interface Onde {
  cidr: string
  conta: string
  vpc: string
  vpc_nome: string
  regiao: string
}

/** Um lado de uma sobreposição. */
export interface LadoSobreposicao {
  cidr: string
  conta: string
  vpc: string
  vpc_nome: string
  regiao: string
}

/**
 * `detalhe` é um saco de propriedades cuja forma depende de `tipo` — é assim que
 * o backend o emite. Tipado como parcial em vez de `any` para o consumo ser
 * checado, e estreitado por `tipo` em quem renderiza.
 */
export interface DetalheDivergencia {
  conta?: string
  regiao?: string
  vpc?: string
  vpc_nome?: string
  projeto?: string
  ambiente?: string
  esperado?: string
  rota?: 'indeterminada' | 'ausente'
  onde?: Onde[]
  confianca?: string
  identico?: boolean
  mesma_conta?: boolean
  a?: LadoSobreposicao
  b?: LadoSobreposicao
}

export interface Divergencia {
  tipo: TipoDivergencia
  cidr: string
  detalhe: DetalheDivergencia
}

export interface DivergenciasResposta {
  itens: Divergencia[]
  rotas: EstadoRotas
  /** Categorias que a falha de leitura do TGW tornou indeterminadas (§6.2). */
  indeterminadas: TipoDivergencia[]
  cobertura: Cobertura
}

export interface PedidoResposta {
  ok: boolean
  pedido_id: number
  /** Conta alvo, ou `null` quando o pedido é de varredura completa. */
  account_id: string | null
  mensagem: string
}

/** GET /api/v1/ipam/ambientes — relação das faixas + cobertura. */
export function getAmbientes(): Promise<AmbientesResposta> {
  return api.get<AmbientesResposta>('/v1/ipam/ambientes').then((r) => r.data)
}

/** GET /api/v1/ipam/divergencias — as 9 categorias + o que ficou indeterminado. */
export function getDivergencias(): Promise<DivergenciasResposta> {
  return api.get<DivergenciasResposta>('/v1/ipam/divergencias').then((r) => r.data)
}

/**
 * POST /api/v1/ipam/collect-request — enfileira uma conciliação.
 *
 * NÃO varre no request: o worker consome no poll seguinte. Varrer aqui é o
 * caminho que reproduz o incidente do §8.1.
 *
 * Com `accountId`, varre só aquela conta — 2 AssumeRole em vez de 146, e é o que
 * dá sentido a um botão por linha. Sem ele, varredura completa na próxima janela.
 */
export function pedirConciliacao(accountId?: string): Promise<PedidoResposta> {
  return api
    .post<PedidoResposta>('/v1/ipam/collect-request',
                          accountId ? { account_id: accountId } : {})
    .then((r) => r.data)
}
